import { useState } from 'react';
import { useSiteData } from '@/contexts/SiteDataContext';
import type { Campaign } from '@/contexts/SiteDataContext';

const URGENCY_OPTIONS = ['low', 'medium', 'high', 'critical'];

export default function CampaignsSection() {
  const { data, updateCampaigns } = useSiteData();
  const [campaigns, setCampaigns] = useState<Campaign[]>([...data.campaigns]);
  const [saved, setSaved] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [searchQ, setSearchQ] = useState('');

  const handleSave = () => { updateCampaigns(campaigns); setSaved(true); setTimeout(() => setSaved(false), 2500); };
  const update = (id: number, field: keyof Campaign, val: string | number) => {
    setCampaigns(campaigns.map((c) => c.id === id ? { ...c, [field]: val } : c));
  };
  const remove = (id: number) => setCampaigns(campaigns.filter((c) => c.id !== id));

  const filtered = campaigns.filter((c) => c.title.toLowerCase().includes(searchQ.toLowerCase()) || c.category.toLowerCase().includes(searchQ.toLowerCase()));

  const getUrgencyColor = (u: string) => ({ critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-yellow-100 text-yellow-700', low: 'bg-emerald-100 text-emerald-700' }[u] || 'bg-background-100 text-foreground-600');

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground-900 mb-1">Campaigns</h2>
        <p className="text-foreground-500 text-sm">Edit campaign titles, goals, raised amounts, and urgency. ({campaigns.length} campaigns)</p>
      </div>

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
        <input
          type="text"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Search campaigns by name or category..."
          className="w-full pl-9 pr-4 py-3 rounded-xl border border-background-200 bg-background-50 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        />
      </div>

      <div className="space-y-3">
        {filtered.map((c) => {
          const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
          return (
            <div key={c.id} className="border border-background-200 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 bg-background-50 cursor-pointer" onClick={() => setExpandedId(expandedId === c.id ? null : c.id)}>
                <img src={c.imageUrl} alt={c.title} className="w-12 h-12 rounded-xl object-cover flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-foreground-900 truncate">{c.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                    <span className="text-xs text-foreground-500">{c.category}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${getUrgencyColor(c.urgency)}`}>{c.urgency}</span>
                    <span className="text-xs text-primary-600 font-semibold">{pct}% funded</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button onClick={(e) => { e.stopPropagation(); remove(c.id); }} className="w-7 h-7 flex items-center justify-center rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-600 transition-colors cursor-pointer">
                    <i className="ri-delete-bin-line text-xs"></i>
                  </button>
                  <i className={`${expandedId === c.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-400`}></i>
                </div>
              </div>

              {expandedId === c.id && (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-background-200 bg-white">
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Campaign Title</label>
                    <input type="text" value={c.title} onChange={(e) => update(c.id, 'title', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Category</label>
                    <input type="text" value={c.category} onChange={(e) => update(c.id, 'category', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Urgency</label>
                    <select value={c.urgency} onChange={(e) => update(c.id, 'urgency', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 cursor-pointer">
                      {URGENCY_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Goal (₹)</label>
                    <input type="number" value={c.goal} onChange={(e) => update(c.id, 'goal', Number(e.target.value))} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Amount Raised (₹)</label>
                    <input type="number" value={c.raised} onChange={(e) => update(c.id, 'raised', Number(e.target.value))} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Days Left</label>
                    <input type="number" value={c.daysLeft} onChange={(e) => update(c.id, 'daysLeft', Number(e.target.value))} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Total Donors</label>
                    <input type="number" value={c.donors} onChange={(e) => update(c.id, 'donors', Number(e.target.value))} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Story / Description</label>
                    <textarea rows={3} value={c.story} onChange={(e) => update(c.id, 'story', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-xs font-semibold text-foreground-600 mb-1">Cover Image URL</label>
                    <input type="text" value={c.imageUrl} onChange={(e) => update(c.id, 'imageUrl', e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-background-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                  </div>
                  {/* Progress bar preview */}
                  <div className="md:col-span-2 p-3 bg-background-50 rounded-xl">
                    <div className="flex justify-between text-xs mb-1.5">
                      <span className="text-foreground-600">₹{c.raised.toLocaleString()} raised</span>
                      <span className="font-bold text-primary-600">{pct}%</span>
                    </div>
                    <div className="h-2 bg-background-200 rounded-full overflow-hidden">
                      <div className="h-full bg-primary-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }}></div>
                    </div>
                    <p className="text-xs text-foreground-400 mt-1">Goal: ₹{c.goal.toLocaleString()}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button onClick={handleSave} className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 whitespace-nowrap ${saved ? 'bg-emerald-500 text-white' : 'bg-primary-500 text-white hover:bg-primary-600'}`}>
        <i className={saved ? 'ri-check-line' : 'ri-save-line'}></i>
        {saved ? 'Saved!' : `Save All Campaigns`}
      </button>
    </div>
  );
}